import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus,
  Pencil,
  Trash2,
  Mail,
  FileText,
  CheckSquare,
  Save,
  RefreshCw,
  Settings,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

const SolicitudesConfigPage = () => {
  // Resoluciones
  const [resoluciones, setResoluciones] = useState([]);
  const [showResolucionDialog, setShowResolucionDialog] = useState(false);
  const [editingResolucion, setEditingResolucion] = useState(null);
  const [resolucionForm, setResolucionForm] = useState({
    nombre: '',
    descripcion: '',
    requiere_email: false,
    plantilla_sugerida_id: '',
    color: '#6b7280',
    activo: true,
    orden: 0
  });

  // Plantillas
  const [plantillas, setPlantillas] = useState([]);
  const [showPlantillaDialog, setShowPlantillaDialog] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [plantillaForm, setPlantillaForm] = useState({
    nombre: '',
    asunto: '',
    contenido: '',
    descripcion: '',
    activo: true
  });
  
  // Preview
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resRes, pltRes] = await Promise.all([
        api.get('/solicitudes-config/resoluciones?solo_activos=false'),
        api.get('/solicitudes-config/plantillas?solo_activas=false')
      ]);
      setResoluciones(resRes.data || []);
      setPlantillas(pltRes.data || []);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  // ===== Resoluciones =====
  
  const openNewResolucion = () => {
    setEditingResolucion(null);
    setResolucionForm({
      nombre: '',
      descripcion: '',
      requiere_email: false,
      plantilla_sugerida_id: '',
      color: '#6b7280',
      activo: true,
      orden: resoluciones.length + 1
    });
    setShowResolucionDialog(true);
  };

  const openEditResolucion = (res) => {
    setEditingResolucion(res);
    setResolucionForm({
      nombre: res.nombre,
      descripcion: res.descripcion || '',
      requiere_email: res.requiere_email || false,
      plantilla_sugerida_id: res.plantilla_sugerida_id || '',
      color: res.color || '#6b7280',
      activo: res.activo !== false,
      orden: res.orden || 0
    });
    setShowResolucionDialog(true);
  };

  const handleSaveResolucion = async () => {
    if (!resolucionForm.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      if (editingResolucion) {
        await api.patch(`/solicitudes-config/resoluciones/${editingResolucion.id}`, resolucionForm);
        toast.success('Resolución actualizada');
      } else {
        await api.post('/solicitudes-config/resoluciones', resolucionForm);
        toast.success('Resolución creada');
      }
      setShowResolucionDialog(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar resolución');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteResolucion = async (id) => {
    if (!window.confirm('¿Eliminar esta resolución?')) return;
    
    try {
      await api.delete(`/solicitudes-config/resoluciones/${id}`);
      toast.success('Resolución eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // ===== Plantillas =====
  
  const openNewPlantilla = () => {
    setEditingPlantilla(null);
    setPlantillaForm({
      nombre: '',
      asunto: '',
      contenido: '',
      descripcion: '',
      activo: true
    });
    setShowPlantillaDialog(true);
  };

  const openEditPlantilla = (plt) => {
    setEditingPlantilla(plt);
    setPlantillaForm({
      nombre: plt.nombre,
      asunto: plt.asunto || '',
      contenido: plt.contenido || '',
      descripcion: plt.descripcion || '',
      activo: plt.activo !== false
    });
    setShowPlantillaDialog(true);
  };

  const handleSavePlantilla = async () => {
    if (!plantillaForm.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      if (editingPlantilla) {
        await api.patch(`/solicitudes-config/plantillas/${editingPlantilla.id}`, plantillaForm);
        toast.success('Plantilla actualizada');
      } else {
        await api.post('/solicitudes-config/plantillas', plantillaForm);
        toast.success('Plantilla creada');
      }
      setShowPlantillaDialog(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar plantilla');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlantilla = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    
    try {
      await api.delete(`/solicitudes-config/plantillas/${id}`);
      toast.success('Plantilla eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };
  
  const handlePreviewPlantilla = async (plantilla) => {
    setPreviewLoading(true);
    setShowPreviewDialog(true);
    
    try {
      const response = await api.post('/solicitudes-config/plantillas/preview', {
        contenido: plantilla.contenido,
        asunto: plantilla.asunto
      });
      setPreviewHtml(response.data.html);
    } catch (error) {
      toast.error('Error generando preview');
      setPreviewHtml('<p>Error al generar el preview</p>');
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const handlePreviewFromForm = async () => {
    if (!plantillaForm.contenido.trim()) {
      toast.error('Ingrese contenido para previsualizar');
      return;
    }
    
    setPreviewLoading(true);
    setShowPreviewDialog(true);
    
    try {
      const response = await api.post('/solicitudes-config/plantillas/preview', {
        contenido: plantillaForm.contenido,
        asunto: plantillaForm.asunto || 'Preview de Email'
      });
      setPreviewHtml(response.data.html);
    } catch (error) {
      toast.error('Error generando preview');
      setPreviewHtml('<p>Error al generar el preview</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  const COLORES = [
    { value: '#dc2626', label: 'Rojo' },
    { value: '#ea580c', label: 'Naranja' },
    { value: '#eab308', label: 'Amarillo' },
    { value: '#16a34a', label: 'Verde' },
    { value: '#0891b2', label: 'Cyan' },
    { value: '#2563eb', label: 'Azul' },
    { value: '#8b5cf6', label: 'Violeta' },
    { value: '#6b7280', label: 'Gris' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="solicitudes-config-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="text-purple-600" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Configuración de Solicitudes</h1>
            <p className="text-slate-500">Gestione resoluciones y plantillas de respuesta</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="resoluciones" className="w-full">
        <TabsList>
          <TabsTrigger value="resoluciones" className="flex items-center gap-2">
            <CheckSquare size={16} />
            Resoluciones ({resoluciones.length})
          </TabsTrigger>
          <TabsTrigger value="plantillas" className="flex items-center gap-2">
            <FileText size={16} />
            Plantillas ({plantillas.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab Resoluciones */}
        <TabsContent value="resoluciones">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Estados de Resolución</CardTitle>
              <Button onClick={openNewResolucion} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Plus size={16} className="mr-2" />
                Nueva Resolución
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Color</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Envía Email</TableHead>
                    <TableHead>Plantilla Sugerida</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resoluciones.map(res => (
                    <TableRow key={res.id}>
                      <TableCell>
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-white shadow"
                          style={{ backgroundColor: res.color }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{res.nombre}</TableCell>
                      <TableCell className="text-slate-500 max-w-xs truncate">{res.descripcion}</TableCell>
                      <TableCell>
                        {res.requiere_email ? (
                          <Badge className="bg-blue-100 text-blue-700">
                            <Mail size={12} className="mr-1" />
                            Sí
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {res.plantilla_sugerida_id ? (
                          <span className="text-sm text-purple-600">
                            {plantillas.find(p => p.id === res.plantilla_sugerida_id)?.nombre || '-'}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={res.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                          {res.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditResolucion(res)}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteResolucion(res.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Plantillas */}
        <TabsContent value="plantillas">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Plantillas de Respuesta</CardTitle>
              <Button onClick={openNewPlantilla} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Plus size={16} className="mr-2" />
                Nueva Plantilla
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Asunto</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plantillas.map(plt => (
                    <TableRow key={plt.id}>
                      <TableCell className="font-medium">{plt.nombre}</TableCell>
                      <TableCell className="text-slate-600 max-w-xs truncate">{plt.asunto}</TableCell>
                      <TableCell className="text-slate-500 max-w-xs truncate">{plt.descripcion}</TableCell>
                      <TableCell>
                        <Badge className={plt.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                          {plt.activo ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-purple-600"
                          onClick={() => handlePreviewPlantilla(plt)}
                          title="Vista previa"
                        >
                          <Eye size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditPlantilla(plt)}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeletePlantilla(plt.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Variables disponibles:</h4>
                <div className="flex flex-wrap gap-2 text-sm">
                  <code className="bg-slate-200 px-2 py-1 rounded">{'{{cliente_nombre}}'}</code>
                  <code className="bg-slate-200 px-2 py-1 rounded">{'{{numero_referencia}}'}</code>
                  <code className="bg-slate-200 px-2 py-1 rounded">{'{{tipo_solicitud}}'}</code>
                  <code className="bg-slate-200 px-2 py-1 rounded">{'{{descripcion}}'}</code>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Resolución */}
      <Dialog open={showResolucionDialog} onOpenChange={setShowResolucionDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingResolucion ? 'Editar Resolución' : 'Nueva Resolución'}
            </DialogTitle>
            <DialogDescription>
              Configure los detalles del estado de resolución
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={resolucionForm.nombre}
                  onChange={(e) => setResolucionForm({ ...resolucionForm, nombre: e.target.value })}
                  placeholder="Ej: Cuenta Bloqueada"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Color</Label>
                <Select 
                  value={resolucionForm.color} 
                  onValueChange={(v) => setResolucionForm({ ...resolucionForm, color: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORES.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: c.value }} />
                          {c.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={resolucionForm.descripcion}
                onChange={(e) => setResolucionForm({ ...resolucionForm, descripcion: e.target.value })}
                placeholder="Descripción del estado..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="requiere_email"
                checked={resolucionForm.requiere_email}
                onCheckedChange={(checked) => setResolucionForm({ ...resolucionForm, requiere_email: checked })}
              />
              <Label htmlFor="requiere_email" className="cursor-pointer">
                Requiere envío de email al cliente
              </Label>
            </div>

            {resolucionForm.requiere_email && (
              <div>
                <Label>Plantilla Sugerida</Label>
                <Select 
                  value={resolucionForm.plantilla_sugerida_id} 
                  onValueChange={(v) => setResolucionForm({ ...resolucionForm, plantilla_sugerida_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccione plantilla..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin plantilla</SelectItem>
                    {plantillas.filter(p => p.activo).map(plt => (
                      <SelectItem key={plt.id} value={plt.id}>{plt.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Checkbox
                id="activo"
                checked={resolucionForm.activo}
                onCheckedChange={(checked) => setResolucionForm({ ...resolucionForm, activo: checked })}
              />
              <Label htmlFor="activo" className="cursor-pointer">Activo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolucionDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveResolucion} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
              {saving ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Plantilla */}
      <Dialog open={showPlantillaDialog} onOpenChange={setShowPlantillaDialog}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </DialogTitle>
            <DialogDescription>
              Configure el contenido de la plantilla de email
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={plantillaForm.nombre}
                  onChange={(e) => setPlantillaForm({ ...plantillaForm, nombre: e.target.value })}
                  placeholder="Ej: Notificación de Bloqueo"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={plantillaForm.descripcion}
                  onChange={(e) => setPlantillaForm({ ...plantillaForm, descripcion: e.target.value })}
                  placeholder="Descripción breve..."
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Asunto del Email</Label>
              <Input
                value={plantillaForm.asunto}
                onChange={(e) => setPlantillaForm({ ...plantillaForm, asunto: e.target.value })}
                placeholder="Ej: Notificación sobre su solicitud - {{numero_referencia}}"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Contenido del Email</Label>
              <Textarea
                value={plantillaForm.contenido}
                onChange={(e) => setPlantillaForm({ ...plantillaForm, contenido: e.target.value })}
                placeholder="Escriba el contenido de la plantilla..."
                className="mt-1 font-mono text-sm"
                rows={10}
              />
              <p className="text-xs text-slate-400 mt-1">
                Use las variables: {'{{cliente_nombre}}'}, {'{{numero_referencia}}'}, {'{{tipo_solicitud}}'}, {'{{descripcion}}'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="activa"
                checked={plantillaForm.activo}
                onCheckedChange={(checked) => setPlantillaForm({ ...plantillaForm, activo: checked })}
              />
              <Label htmlFor="activa" className="cursor-pointer">Activa</Label>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <Button 
              type="button"
              variant="outline" 
              onClick={handlePreviewFromForm}
              disabled={!plantillaForm.contenido.trim()}
              className="text-purple-600 border-purple-300"
            >
              <Eye size={14} className="mr-2" />
              Vista Previa
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPlantillaDialog(false)}>Cancelar</Button>
              <Button onClick={handleSavePlantilla} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
                {saving ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                Guardar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Preview */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="text-purple-600" />
              Vista Previa del Email
            </DialogTitle>
            <DialogDescription>
              Así se verá el email con datos de ejemplo
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 border rounded-lg overflow-hidden bg-slate-100" style={{ height: '500px' }}>
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <RefreshCw className="animate-spin text-purple-600" size={32} />
              </div>
            ) : (
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                className="w-full h-full border-0"
                sandbox="allow-same-origin"
              />
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolicitudesConfigPage;
